import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useHistory, useParams } from "react-router-dom";
import { HiOutlineTrash } from "react-icons/hi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import { capture } from "../../instrument";
import { INPUT_CLASS_NAME, isValidIp } from "../../utils";

const parseLines = (text) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const EditNetwork = () => {
  const history = useHistory();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [originsText, setOriginsText] = useState("");
  const [indexNamesText, setIndexNamesText] = useState("");
  const [allowedIpsText, setAllowedIpsText] = useState("");
  const [configSharing, setConfigSharing] = useState(false);

  const {
    data: network,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["network", id],
    queryFn: async () => {
      const response = await API.get(`/admin/network/${id}`);
      if (!response.ok) throw new Error(response.message || "Impossible de charger le réseau");
      return response.data;
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!network) return;
    setName(network.name);
    setOriginsText((network.origins || []).join("\n"));
    setIndexNamesText((network.indexNames || []).join("\n"));
    setAllowedIpsText((network.allowedIps || []).join("\n"));
    setConfigSharing(!!network.configSharing);
  }, [network]);

  useEffect(() => {
    if (!isError) return;
    toast.error("Une erreur est survenue lors du chargement du réseau");
    history.push("/network");
  }, [isError]);

  const updateMutation = useMutation({
    mutationFn: (body) => API.put(`/admin/network/${id}`, body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Réseau mis à jour avec succès");
      queryClient.invalidateQueries({ queryKey: ["networks"] });
      queryClient.invalidateQueries({ queryKey: ["network", id] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => API.delete(`/admin/network/${id}`),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Réseau supprimé avec succès");
      queryClient.invalidateQueries({ queryKey: ["networks"] });
      history.push("/network");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const isMutating = updateMutation.isPending || deleteMutation.isPending;

  const updateNetwork = () => {
    if (!name) return toast.error("Veuillez renseigner le nom du réseau");
    const allowedIps = parseLines(allowedIpsText);
    const invalidIp = allowedIps.find((ip) => !isValidIp(ip));
    if (invalidIp) return toast.error(`IP invalide : ${invalidIp}`);
    updateMutation.mutate({ name, origins: parseLines(originsText), indexNames: parseLines(indexNamesText), allowedIps, configSharing });
  };

  const deleteNetwork = () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce réseau ?")) return;
    deleteMutation.mutate();
  };

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <h1 className="truncate text-lg font-semibold text-gray-900">{network?.name || "Réseau"}</h1>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
            onClick={deleteNetwork}
            disabled={isMutating}
          >
            <HiOutlineTrash className="h-4 w-4" />
            Supprimer
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={updateNetwork}
            disabled={isMutating || isLoading}
          >
            {updateMutation.isPending ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
          <span className="text-sm text-gray-400">Chargement...</span>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-4xl px-8 py-6">
            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Nom*</label>
                <input className={INPUT_CLASS_NAME} placeholder="Nom du réseau" value={name} onChange={(e) => setName(e.target.value)} disabled={isMutating} />
              </div>
              <div className="flex items-start gap-4 px-5 py-3.5">
                <div className="w-64 shrink-0 pt-2">
                  <span className="text-[13px] font-medium text-gray-600">Origines</span>
                  <span className="block text-xs text-gray-500">Une origine par ligne (ex: https://b2a.querco.co)</span>
                </div>
                <textarea
                  className={`${INPUT_CLASS_NAME} min-h-[100px] resize-y font-mono text-xs`}
                  placeholder={"https://b2a.querco.co\nhttps://admission.b2a.fr"}
                  value={originsText}
                  onChange={(e) => setOriginsText(e.target.value)}
                  disabled={isMutating}
                />
              </div>
              <div className="flex items-start gap-4 px-5 py-3.5">
                <div className="w-64 shrink-0 pt-2">
                  <span className="text-[13px] font-medium text-gray-600">IndexNames</span>
                  <span className="block text-xs text-gray-500">Un indexName par ligne</span>
                </div>
                <textarea
                  className={`${INPUT_CLASS_NAME} min-h-[100px] resize-y font-mono text-xs`}
                  placeholder={"b2avosges\nb2abiorhin\nb2abiolia"}
                  value={indexNamesText}
                  onChange={(e) => setIndexNamesText(e.target.value)}
                  disabled={isMutating}
                />
              </div>
              <div className="flex items-start gap-4 px-5 py-3.5">
                <div className="w-64 shrink-0">
                  <span className="text-[13px] font-medium text-gray-600">Partage de configuration</span>
                  <span className="block text-xs text-gray-500">Les sites du réseau peuvent partager questions, actions et recommandations entre eux.</span>
                </div>
                <label className="flex cursor-pointer items-center gap-2 pt-0.5">
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-1 focus:ring-primary-500"
                    checked={configSharing}
                    onChange={(e) => setConfigSharing(e.target.checked)}
                    disabled={isMutating}
                  />
                  <span className="text-sm text-gray-700">Activé</span>
                </label>
              </div>
              <div className="flex items-start gap-4 px-5 py-3.5">
                <div className="w-64 shrink-0 pt-2">
                  <span className="text-[13px] font-medium text-gray-600">IP autorisées</span>
                  <span className="block text-xs text-gray-500">
                    Une IP par ligne — vide = accès sans restriction. Les comptes du réseau ne pourront se connecter à l'admin que depuis ces IP (hors superadmin).
                  </span>
                </div>
                <textarea
                  className={`${INPUT_CLASS_NAME} min-h-[100px] resize-y font-mono text-xs`}
                  placeholder={"178.23.156.132"}
                  value={allowedIpsText}
                  onChange={(e) => setAllowedIpsText(e.target.value)}
                  disabled={isMutating}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditNetwork;
